import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorText, formatDate } from '../api';
import { useAuth } from '../auth';
import { VorgangKarte } from '../components/Vorgang';
import { Empty, Loading, Notice } from '../components/ui';
import type { Bestellung, Frist, Lieferung, Serviceauftrag, Standort, Vorgang, VorgangArt } from '../types';

const ANSICHTEN = [
  { schluessel: 'offen', label: 'Alle offenen' },
  { schluessel: 'meine', label: 'Meine' },
  { schluessel: 'ueberfaellig', label: 'Überfällig' },
  { schluessel: 'abgeschlossen', label: 'Abgeschlossen' },
] as const;

export type Liste = 'bestellung' | 'werkstatt' | 'aussendienst' | 'lieferung';

interface Info {
  art: VorgangArt;
  titel: string;
  neu: string;
  neuLabel: string;
  api: string;
  extra?: Record<string, string>;
  /** Status, die zu dieser Liste gehören (für den Filter). */
  status?: string[];
  suche: string;
  titelVon: (v: Vorgang) => string;
}

const geraet = (v: Vorgang) => {
  const s = v as Serviceauftrag;
  return [s.geraeteart, s.marke, s.modell].filter(Boolean).join(' ');
};

const INFO: Record<Liste, Info> = {
  bestellung: {
    art: 'bestellung',
    titel: 'Bestellungen',
    neu: '/bestellungen/neu',
    neuLabel: '+ Neue Bestellung',
    api: '/api/bestellungen',
    suche: 'Nummer, Kunde, Artikel, Lieferant …',
    titelVon: (v) => (v as Bestellung).artikel ?? 'Bestellung',
  },
  werkstatt: {
    art: 'service',
    titel: 'Werkstatt',
    neu: '/service/neu',
    neuLabel: '+ Gerät annehmen',
    api: '/api/service',
    extra: { art: 'werkstatt' },
    status: ['angenommen', 'diagnose', 'kva_beim_kunden', 'teile_bestellt', 'in_reparatur', 'abholbereit', 'kunde_informiert'],
    suche: 'Nummer, Kunde, Gerät, Seriennummer …',
    titelVon: geraet,
  },
  aussendienst: {
    art: 'service',
    titel: 'Außendienst',
    neu: '/service/neu?art=aussendienst',
    neuLabel: '+ Außendienstauftrag',
    api: '/api/service',
    extra: { art: 'aussendienst' },
    status: ['termin_offen', 'termin_vereinbart', 'teile_bestellt'],
    suche: 'Nummer, Kunde, Gerät …',
    titelVon: geraet,
  },
  lieferung: {
    art: 'lieferung',
    titel: 'Lieferungen',
    neu: '/lieferungen/neu',
    neuLabel: '+ Neue Lieferung',
    api: '/api/lieferungen',
    suche: 'Nummer, Kunde, Artikel, Ort …',
    titelVon: (v) => {
      const l = v as Lieferung;
      return l.termin_datum ? `${l.artikel} · ${formatDate(l.termin_datum)}` : l.artikel;
    },
  },
};

/** Gemeinsame Liste für Bestellungen, Werkstatt, Außendienst und Lieferungen. */
export function VorgangListePage({ liste }: { liste: Liste }) {
  const info = INFO[liste];
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const ansicht = params.get('ansicht') ?? 'offen';
  const status = params.get('status') ?? '';
  const standort = params.get('standort') ?? '';
  const [q, setQ] = useState('');
  const [eintraege, setEintraege] = useState<Vorgang[] | null>(null);
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [standorte, setStandorte] = useState<Standort[]>([]);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    void api<Frist[]>('/api/einstellungen/fristen').then((f) =>
      setFristen(f.filter((x) => x.vorgang_art === info.art && (!info.status || info.status.includes(x.status)))),
    );
    void api<Standort[]>('/api/standorte').then(setStandorte);
  }, [info]);

  useEffect(() => {
    setEintraege(null);
    const zeit = window.setTimeout(() => {
      api<Vorgang[]>(info.api, { query: { ansicht, status, standort, q, ...info.extra } })
        .then(setEintraege)
        .catch((e) => setFehler(errorText(e)));
    }, 200);
    return () => window.clearTimeout(zeit);
  }, [info, ansicht, status, standort, q]);

  const setze = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  // Lieferungen legt nur der Laden an.
  const darfNeu = liste !== 'lieferung' || can('admin', 'verkauf');

  return (
    <>
      <div className="kopf">
        <h1>{info.titel}</h1>
        <span className="spacer" />
        {darfNeu && (
          <Link to={info.neu} className="button primary">
            {info.neuLabel}
          </Link>
        )}
      </div>
      <div className="tabs">
        {ANSICHTEN.map((a) => (
          <button key={a.schluessel} className={a.schluessel === ansicht ? 'aktiv' : ''} onClick={() => setze('ansicht', a.schluessel)}>
            {a.label}
          </button>
        ))}
      </div>
      <div className="row" style={{ marginBottom: 14, flexWrap: 'nowrap' }}>
        <input placeholder={info.suche} value={q} onChange={(e) => setQ(e.target.value)} />
        {ansicht !== 'abgeschlossen' && (
          <select value={status} onChange={(e) => setze('status', e.target.value)} style={{ width: 200 }}>
            <option value="">Alle Status</option>
            {fristen.map((f) => (
              <option key={f.status} value={f.status}>
                {f.bezeichnung}
              </option>
            ))}
          </select>
        )}
        <select value={standort} onChange={(e) => setze('standort', e.target.value)} style={{ width: 160 }}>
          <option value="">Alle Standorte</option>
          {standorte.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}
      {!eintraege ? (
        <Loading />
      ) : eintraege.length === 0 ? (
        <Empty>Nichts in dieser Ansicht.</Empty>
      ) : (
        <div className="ticketliste">
          {eintraege.map((v) => (
            <VorgangKarte key={v.id} art={info.art} v={v} titel={info.titelVon(v)} zeigeZustaendig={ansicht !== 'meine'} />
          ))}
        </div>
      )}
    </>
  );
}
